<script lang="ts" setup>
import type { IconKeys } from "../ui/Icon/Icon.interface"
import type { FlaggedContent } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { onClickOutside } from "@vueuse/core"
import { computed, ref, useTemplateRef } from "vue"
import { FLAGGED_CONTENT_QUERY_KEY, useReviewFlaggedContent } from "@/api"
import { Button, Expandable, Icon } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useTranslation } from "@/composables"
import { FlaggedContentStatus, FlaggedContentType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"

const props = defineProps<{
  content: FlaggedContent
}>()

const eventVaultStore = useEventVaultStore()

const toast = useToast()
const { d, t } = useTranslation()
const queryClient = useQueryClient()

type PendingAction = "keep-hidden" | "restore" | null
const pendingAction = ref<PendingAction>(null)

function cancelAction() {
  pendingAction.value = null
}

const { mutate: mutateReviewContent, isPending: isReviewFlaggedContentPending } = useReviewFlaggedContent()

function confirmAction() {
  if (!eventVaultStore.eventVaultId || !props.content.contentType || !props.content.contentId) {
    toast.error()
    return
  }

  mutateReviewContent({ eventVaultId: eventVaultStore.eventVaultId, reviewRequest: {
    contentType: props.content.contentType,
    contentId: props.content.contentId,
    status: pendingAction.value === "keep-hidden" ? FlaggedContentStatus.APPROVED : FlaggedContentStatus.REJECTED,
  } }, {
    onSuccess: () => {
      toast.success({ description: t("flagged_content_data.success_toast") })
      queryClient.invalidateQueries({ queryKey: [FLAGGED_CONTENT_QUERY_KEY] })
      pendingAction.value = null
    },
    onError: () => {
      toast.error()
    },
  })
}

const statusDetails = computed<{ label: string, class: string, icon: IconKeys } | undefined>(() => {
  const status = props.content.status
  if (status === FlaggedContentStatus.PENDING || status === FlaggedContentStatus.UNDER_REVIEW)
    return { label: t("flagged_content_data.status_labels.pending_review"), class: "bg-amber-100 text-amber-800", icon: "Clock" }
  if (status === FlaggedContentStatus.APPROVED)
    return { label: t("flagged_content_data.status_labels.hidden"), class: "bg-red-100 text-red-800", icon: "EyeOff" }
  if (status === FlaggedContentStatus.REJECTED)
    return { label: t("flagged_content_data.status_labels.restored"), class: "bg-green-100 text-green-800", icon: "EyeOn" }

  return undefined
})

const contentTypeIcon = computed<IconKeys | undefined>(() => {
  if (props.content.contentType === FlaggedContentType.PHOTO)
    return "ImageOn"
  if (props.content.contentType === FlaggedContentType.VIDEO)
    return "Video"
  if (props.content.contentType === FlaggedContentType.MESSAGE)
    return "BookOpen"
  if (props.content.contentType === FlaggedContentType.COMMENT)
    return "ChatText"

  return undefined
})

const showMediaPreview = ref(false)
const mediaPreviewPanelRef = useTemplateRef<HTMLElement | null>("mediaPreviewPanelRef")

function openMediaPreview() {
  showMediaPreview.value = true
}

function closeMediaPreview() {
  showMediaPreview.value = false
}

onClickOutside(mediaPreviewPanelRef, () => {
  if (showMediaPreview.value)
    closeMediaPreview()
})
</script>

<template>
  <div class="overflow-hidden p-2 md:p-4 border rounded-xl shadow-xs font-montserrat" data-test="flagged-content-item">
    <div class="flex flex-col gap-3">
      <div class="rounded-lg border border-secondary bg-white overflow-hidden" data-test="flagged-content-media-preview">
        <div class="flex items-center gap-2 px-4 py-2 bg-footer-bg">
          <Icon v-if="contentTypeIcon" :icon="contentTypeIcon" size="sm" />
          <span class="text-xs font-semibold uppercase tracking-wide">
            {{ content.contentType }}
          </span>
        </div>
        <div v-if="content.previewUrl" class="flex gap-4 p-4">
          <button
            type="button"
            class="shrink-0 w-28 h-28 rounded-lg overflow-hidden border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:opacity-90 transition-opacity flex items-center justify-center bg-footer-bg"
            data-test="flagged-content-preview-trigger"
            @click="openMediaPreview"
          >
            <img
              v-if="content.contentType !== FlaggedContentType.VIDEO"
              :src="content.previewUrl"
              :alt="`${content.contentType} ${content.contentId}`"
              class="w-full h-full object-cover pointer-events-none"
              data-test="flagged-content-preview"
            >
            <Icon
              v-else
              icon="Play"
              size="xl"
              class="text-primary pointer-events-none"
              data-test="flagged-content-preview-video"
            />
          </button>
          <div class="min-w-0 flex-1 flex flex-col justify-center text-sm">
            <span class="font-medium">{{ t("flagged_content_data.uploaded_by_label") }}</span>
            <span>{{ content.contentBy }}</span>
          </div>
        </div>
        <div v-else class="flex gap-4 p-4 text-sm">
          <div class="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-footer-bg">
            <Icon v-if="contentTypeIcon" :icon="contentTypeIcon" size="lg" />
          </div>
          <div class="min-w-0 flex-1">
            <blockquote class="border-l-2 pl-3 py-1 my-1 italic">
              "{{ content?.message || content?.comment }}"
            </blockquote>
            <p class="mt-2">
              <span class="font-medium">{{ t("flagged_content_data.from_label") }}</span> {{ content?.contentBy }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-between items-end gap-2 flex-wrap-reverse text-sm">
        <div class="flex flex-col gap-1">
          <div class="flex flex-col gap-y-1">
            <span v-if="content.lastFlaggedDate || content.firstFlaggedDate" data-test="flagged-content-last-flagged">{{ t("flagged_content_data.last_flagged_label", { date: d((content.lastFlaggedDate || content.firstFlaggedDate)!, "long") }) }}</span>
          </div>
          <div v-if="content.reasons?.length" class="flex flex-wrap gap-1" data-test="flagged-content-reasons">
            {{ t("flagged_content_data.reason_label") }}
            <span>{{ content.reasons.join(", ") }}</span>
          </div>

          <Expandable v-if="content.flags?.length">
            <template #activator="{ toggle, isOpen, activatorAttrs }">
              <button
                v-bind="activatorAttrs"
                type="button"
                class="cursor-pointer text-primary list-none font-black flex items-center hover:underline"
                data-test="flagged-content-flag-details-toggle"
                @click="toggle"
              >
                {{ t("flagged_content_data.view_flag_details_btn", { count: content.flags.length }) }}
                <Icon icon="CircleChevronDown" size="sm" class="ml-2 transition-transform duration-200 ease-in-out text-primary" :class="{ 'rotate-180': isOpen }" />
              </button>
            </template>
            <ul class="mt-2 pl-4 max-w-3xl flex flex-col gap-3">
              <li
                v-for="(flag, index) in content.flags"
                :key="flag.id"
                class="px-3 border-l-2 py-1"
                :class="index % 2 === 1 ? 'bg-gray-100' : 'bg-white'"
                data-test="flagged-content-flag-detail"
              >
                <p>{{ d(flag.dateCreated, "long") }}</p>
                <p>
                  <span v-if="flag.reason" class="font-medium">{{ t("flagged_content_data.reported_reason_label", { reason: flag.reason }) }}</span>
                  <span v-if="flag.reporterName"> — {{ t("flagged_content_data.reported_by_label", { name: flag.reporterName }) }}</span>
                  <span v-if="flag.reasonDetails"> — "{{ flag.reasonDetails }}"</span>
                </p>
                <p>
                  <span v-if="flag.reviewedByName" class="font-medium">{{ t("flagged_content_data.reviewed_by_label", { name: flag.reviewedByName }) }}</span>
                  <span v-if="flag.reviewedAt"> {{ t("flagged_content_data.reviewed_at_label", { date: d(flag.reviewedAt, "long") }) }}</span>
                  <span v-if="flag.reviewNotes"> — {{ flag.reviewNotes }}</span>
                </p>
              </li>
            </ul>
          </Expandable>
        </div>

        <div class="flex items-center gap-1">
          <span class="font-semibold">{{ t("flagged_content_data.status_label") }}</span>
          <span
            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize"
            :class="statusDetails?.class"
            data-test="flagged-content-status"
          >
            <Icon v-if="statusDetails?.icon" :icon="statusDetails.icon" size="sm" class="mr-1" />
            {{ statusDetails?.label }}
          </span>
        </div>
      </div>

      <!-- review actions -->
      <div v-if="content.status === FlaggedContentStatus.PENDING" class="flex flex-wrap items-center gap-2 pt-2 justify-end" data-test="flagged-content-actions">
        <template v-if="pendingAction">
          <p class="text-sm">
            {{ pendingAction === 'keep-hidden'
              ? t("flagged_content_data.confirm_hide_content_question")
              : t("flagged_content_data.confirm_restore_content_question") }}
          </p>
          <Button
            variant="flat"
            :disabled="isReviewFlaggedContentPending"
            size="sm"
            data-test="flagged-content-confirm-cancel"
            class="flex-1 md:flex-none"
            @click="cancelAction"
          >
            {{ t("cancel") }}
          </Button>
          <Button
            :variant="pendingAction === 'keep-hidden' ? 'danger' : 'primary'"
            :disabled="isReviewFlaggedContentPending"
            size="sm"
            :data-test="pendingAction === 'keep-hidden' ? 'flagged-content-confirm-keep-hidden' : 'flagged-content-confirm-restore'"
            class="flex-1 md:flex-none"
            @click="confirmAction"
          >
            <Icon v-if="isReviewFlaggedContentPending" icon="Loading" size="sm" />
            {{ isReviewFlaggedContentPending ? t("flagged_content_data.confirming_btn") : t("confirm") }}
          </Button>
        </template>
        <template v-else>
          <Button
            v-if="content.status === FlaggedContentStatus.PENDING || content.status === FlaggedContentStatus.UNDER_REVIEW || content.status === FlaggedContentStatus.REJECTED"
            prefix-icon="EyeOff"
            variant="danger"
            size="sm"
            data-test="flagged-content-action-reject"
            class="flex-1 md:flex-none"
            @click="pendingAction = 'keep-hidden'"
          >
            {{ t("flagged_content_data.hide_btn") }}
          </Button>
          <Button
            v-if="content.status === FlaggedContentStatus.PENDING || content.status === FlaggedContentStatus.UNDER_REVIEW || content.status === FlaggedContentStatus.APPROVED"
            prefix-icon="RotateCcw"
            variant="primary"
            size="sm"
            data-test="flagged-content-action-approve"
            class="flex-1 md:flex-none"
            @click="pendingAction = 'restore'"
          >
            {{ t("flagged_content_data.restore_btn") }}
          </Button>
        </template>
      </div>

      <!-- preview popup -->
      <Teleport to="body">
        <Transition name="gpx-fade">
          <div v-if="showMediaPreview && content.previewUrl" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-test="flagged-content-preview-popup">
            <div ref="mediaPreviewPanelRef" class="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center bg-black/90 rounded-lg overflow-hidden shadow-xl">
              <Button variant="flat" prefix-icon="Close" size="sm" data-test="flagged-content-preview-close" class="absolute top-2 right-2 z-10" @click="closeMediaPreview" />
              <video v-if="content.contentType === FlaggedContentType.VIDEO" :src="content.previewUrl" controls class="max-w-full max-h-[90vh] w-auto h-auto object-contain" data-test="flagged-content-preview-video" />
              <img v-else :src="content.previewUrl" :alt="`${content.contentType} ${content.contentId}`" class="max-w-full max-h-[90vh] w-auto h-auto object-contain" data-test="flagged-content-preview-image">
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>
